/**
 * Query-key factory for HQ board checklist REST.
 *
 *   all
 *   board(boardId)
 *   boardList(boardId, archived, listFilters) — Checklists view rail / progress overview
 *   entity(boardId, entityType, entityId)    — entity Manager list
 *   detail(boardId, checklistId)             — retrieve
 *   statusMatrix(boardId, checklistId, to)   — Checklists view day matrix (`to` omitted = current)
 *   itemStatusLog(boardId, itemId, user)     — cell history (`user` omitted = all assignees)
 */

export type ChecklistEntityPathType = "task" | "resource" | "client";

/** Map a record's entityType (or FK id key) to the checklist path segment. */
export function checklistEntityPathType(
  entityType: string | undefined
): ChecklistEntityPathType | null {
  if (entityType === "taskId" || entityType === "task") return "task";
  if (entityType === "resourceId" || entityType === "resource") return "resource";
  if (entityType === "clientId" || entityType === "client") return "client";
  return null;
}

export type BoardChecklistListFilters = {
  progress?: string | null;
  assignedTo?: number | null;
  filterEntityType?: ChecklistEntityPathType | null;
  createdWithinDays?: 7 | 30 | 90 | null;
  search?: string | null;
  recurrence?: string | null;
};

export const checklistKeys = {
  all: ["checklists"] as const,

  board: (boardId: number) => [...checklistKeys.all, boardId] as const,

  boardList: (boardId: number, archived = false, listFilters: BoardChecklistListFilters = {}) =>
    [
      ...checklistKeys.board(boardId),
      "board-list",
      {
        archived,
        progress: listFilters.progress ?? null,
        assignedTo: listFilters.assignedTo ?? null,
        filterEntityType: listFilters.filterEntityType ?? null,
        createdWithinDays: listFilters.createdWithinDays ?? null,
        search: listFilters.search ?? null,
        recurrence: listFilters.recurrence ?? null
      }
    ] as const,

  entity: (boardId: number, entityType: ChecklistEntityPathType, entityId: number | string) =>
    [...checklistKeys.board(boardId), "list", entityType, entityId] as const,

  detail: (boardId: number, checklistId: number | string) =>
    [...checklistKeys.board(boardId), "detail", checklistId] as const,

  statusMatrix: (boardId: number, checklistId: number | string, to?: string | null) =>
    [...checklistKeys.board(boardId), "status-matrix", checklistId, to ?? "current"] as const,

  itemStatusLog: (boardId: number, itemId: number | string, boardUserId?: number | null) =>
    [...checklistKeys.board(boardId), "item-status-log", itemId, boardUserId ?? "all"] as const
} as const;
